import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

// ─────────────────────────────────────────────
// SOUND ENGINE
// ─────────────────────────────────────────────
const SoundEngine = (() => {
  let ctx = null;
  let enabled = true;
  let lastPlayed = {};
  const DEBOUNCE = 80;

  const init = () => {
    if (!ctx) ctx = new (window.AudioContext || window.webkitAudioContext)();
  };

  const configs = {
    click:   [{ f: 520,  t: 'sine',     d: 0.08, v: 0.18 }],
    hover:   [{ f: 660,  t: 'sine',     d: 0.05, v: 0.06 }],
    open:    [{ f: 380,  t: 'triangle', d: 0.18, v: 0.12 }, { f: 600, t: 'sine', d: 0.25, v: 0.1 }],
    close:   [{ f: 300,  t: 'triangle', d: 0.12, v: 0.09 }],
    select:  [{ f: 780,  t: 'square',   d: 0.1,  v: 0.09 }],
    coin:    [{ f: 1400, t: 'sine',     d: 0.28, v: 0.2  }, { f: 1800, t: 'sine', d: 0.18, v: 0.12 }],
    success: [{ f: 880,  t: 'sine',     d: 0.15, v: 0.2  }, { f: 1100, t: 'sine', d: 0.25, v: 0.2 }, { f: 1320, t: 'sine', d: 0.35, v: 0.18 }],
    engine:  [{ f: 120,  t: 'sawtooth', d: 0.3,  v: 0.08 }],
    unlock:  [{ f: 600,  t: 'sine',     d: 0.08, v: 0.15 }, { f: 900, t: 'sine', d: 0.12, v: 0.15 }, { f: 1200, t: 'sine', d: 0.22, v: 0.18 }],
  };

  const play = (type) => {
    if (!enabled) return;
    const now = Date.now();
    if (lastPlayed[type] && now - lastPlayed[type] < DEBOUNCE) return;
    lastPlayed[type] = now;
    try {
      init();
      const notes = configs[type] || configs.click;
      notes.forEach(({ f, t, d, v }, i) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.connect(g); g.connect(ctx.destination);
        o.type = t;
        const start = ctx.currentTime + i * 0.06;
        o.frequency.setValueAtTime(f, start);
        if (type === 'engine') o.frequency.linearRampToValueAtTime(f * 2.5, start + d);
        g.gain.setValueAtTime(v, start);
        g.gain.exponentialRampToValueAtTime(0.001, start + d);
        o.start(start); o.stop(start + d + 0.01);
      });
    } catch (e) {}
  };

  return { play, toggle: () => { enabled = !enabled; return enabled; }, isEnabled: () => enabled };
})();

// ─────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────
const BADGES = {
  FIRST_ASSESSMENT:  { id: 'first_assessment',  name: 'First Steps',     icon: '👣', color: '#4A90E2', rarity: 'common'    },
  PERFECT_SCORE:     { id: 'perfect_score',      name: 'Perfect Score',   icon: '💯', color: '#FFD700', rarity: 'legendary' },
  SPEED_DEMON:       { id: 'speed_demon',        name: 'Speed Demon',     icon: '⚡', color: '#FF6B9D', rarity: 'rare'      },
  SUPER_HARD_ATTEMPT:{ id: 'super_hard_attempt', name: 'Fearless',        icon: '🦁', color: '#FF8B5A', rarity: 'epic'      },
  STREAK_MASTER:     { id: 'streak_master',      name: 'Streak Master',   icon: '🔥', color: '#FF6B6B', rarity: 'rare'      },
  CONCEPT_MASTER:    { id: 'concept_master',     name: 'Concept Master',  icon: '🏆', color: '#50C878', rarity: 'epic'      },
  DAILY_CHAMPION:    { id: 'daily_champion',     name: 'Daily Champ',     icon: '📅', color: '#9B6FD8', rarity: 'rare'      },
  PRACTICE_PRO:      { id: 'practice_pro',       name: 'Practice Pro',    icon: '🎮', color: '#FFA500', rarity: 'common'    },
};

const MENU_ITEMS = [
  { id: 'garage',    label: 'Start Assessment',    icon: '🏎️',  sub: 'Your Assessment',     color: '#FF6B2B', glow: '#FF6B2B',  route: '/assessment',             locked: false },
  { id: 'missions',  label: 'Daily Mission',  icon: '🎯',  sub: 'Daily & Weekly',    color: '#4ECDC4', glow: '#4ECDC4',  route: '/daily-assessment',       locked: false },
  { id: 'rewards',   label: 'Rewards',   icon: '🏆',  sub: 'Your Achievements', color: '#FFD700', glow: '#FFD700',  route: null,                      locked: false, panel: 'rewards'  },
  { id: 'track',     label: 'Practice Track',      icon: '🏁',  sub: 'Practice Now',      color: '#FF4081', glow: '#FF4081',  route: '/select-practice-concept',locked: false },
  { id: 'map',       label: 'Progress',  icon: '🗺️',  sub: 'View Progress',     color: '#7C4DFF', glow: '#7C4DFF',  route: null,                      locked: false, panel: 'progress' },
  { id: 'settings',  label: 'Settings',  icon: '⚙️',  sub: 'Preferences',       color: '#78909C', glow: '#90A4AE',  route: null,                      locked: false, panel: 'settings' },
];

// ─────────────────────────────────────────────
// PARTICLE SYSTEM
// ─────────────────────────────────────────────
const ParticleField = () => {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animId;
    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    resize();
    window.addEventListener('resize', resize);
    const particles = Array.from({ length: 55 }, () => ({
      x: Math.random() * canvas.width, y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.4, vy: -Math.random() * 0.6 - 0.2,
      r: Math.random() * 2.5 + 0.5,
      alpha: Math.random() * 0.5 + 0.15,
      color: ['#FF6B2B','#FFD700','#4ECDC4','#FF4081','#7C4DFF','#4A90E2'][Math.floor(Math.random()*6)],
    }));
    const tick = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach(p => {
        p.x += p.vx; p.y += p.vy;
        if (p.y < -5) { p.y = canvas.height + 5; p.x = Math.random() * canvas.width; }
        if (p.x < -5 || p.x > canvas.width + 5) p.x = Math.random() * canvas.width;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = p.color + Math.floor(p.alpha * 255).toString(16).padStart(2, '0');
        ctx.fill();
      });
      animId = requestAnimationFrame(tick);
    };
    tick();
    return () => { cancelAnimationFrame(animId); window.removeEventListener('resize', resize); };
  }, []);
  return <canvas ref={canvasRef} style={{ position:'fixed', inset:0, pointerEvents:'none', zIndex:0 }} />;
};

// ─────────────────────────────────────────────
// COIN FLY-TO-TARGET ANIMATION
// ─────────────────────────────────────────────
const useCoinFlyAnimation = () => {
  const coinDisplayRef = useRef(null);

  const flyCoinsToTarget = useCallback((count = 16) => {
    const target = coinDisplayRef.current;
    if (!target) return;
    const targetRect = target.getBoundingClientRect();
    const destX = targetRect.left + targetRect.width / 2;
    const destY = targetRect.top + targetRect.height / 2;

    // Spawn coins from center-bottom of screen, arc toward coin display
    const originX = window.innerWidth / 2;
    const originY = window.innerHeight * 0.55;

    for (let i = 0; i < count; i++) {
      setTimeout(() => {
        SoundEngine.play('coin');

        const el = document.createElement('div');
        const spreadX = (Math.random() - 0.5) * 220;
        const spreadY = -(Math.random() * 160 + 60);
        // midpoint scatter
        const midX = originX + spreadX;
        const midY = originY + spreadY;

        el.style.cssText = `
          position: fixed;
          left: ${originX}px;
          top: ${originY}px;
          font-size: 22px;
          pointer-events: none;
          z-index: 9999;
          transform: translate(-50%, -50%) scale(1);
          transition: none;
          will-change: transform, opacity;
        `;
        el.textContent = '🪙';
        document.body.appendChild(el);

        // Phase 1: burst outward
        requestAnimationFrame(() => {
          el.style.transition = `left ${0.25 + Math.random() * 0.1}s cubic-bezier(.2,.8,.4,1), top ${0.25 + Math.random() * 0.1}s cubic-bezier(.2,.8,.4,1), transform 0.25s ease`;
          el.style.left = `${midX}px`;
          el.style.top = `${midY}px`;
          el.style.transform = `translate(-50%,-50%) scale(1.3) rotate(${(Math.random()-0.5)*60}deg)`;
        });

        // Phase 2: fly to coin display
        setTimeout(() => {
          el.style.transition = `left 0.45s cubic-bezier(.55,0,.7,1), top 0.45s cubic-bezier(.55,0,.7,1), transform 0.45s ease, opacity 0.15s 0.35s ease`;
          el.style.left = `${destX}px`;
          el.style.top = `${destY}px`;
          el.style.transform = `translate(-50%,-50%) scale(0.3) rotate(${(Math.random()-0.5)*180}deg)`;
          el.style.opacity = '0';

          // Flash the coin display on each coin arrival
          setTimeout(() => {
            if (target) {
              target.style.transform = 'scale(1.22)';
              target.style.boxShadow = '0 0 28px #FFD700CC';
              setTimeout(() => {
                target.style.transform = 'scale(1)';
                target.style.boxShadow = '';
              }, 160);
            }
            el.remove();
          }, 450);
        }, 300 + i * 28);

      }, i * 55);
    }
  }, []);

  return { coinDisplayRef, flyCoinsToTarget };
};

// ─────────────────────────────────────────────
// CARD FLY-INTO-PANEL ANIMATION
// ─────────────────────────────────────────────
// Each card flies from a random scatter position around the screen into its slot in the panel
const CardFlyIn = ({ children, index, panelOpen, targetRef, delay = 0 }) => {
  const cardRef = useRef(null);
  const [style, setStyle] = useState({ opacity: 0, transform: 'scale(0)', pointerEvents: 'none' });
  const [settled, setSettled] = useState(false);

  useEffect(() => {
    if (!panelOpen) {
      setStyle({ opacity: 0, transform: 'scale(0)', pointerEvents: 'none' });
      setSettled(false);
      return;
    }

    // Random scatter origin: from outside the viewport toward this card's final slot
    const scatterOrigins = [
      { x: '-120vw', y: '-40vh' },
      { x: '120vw',  y: '-60vh' },
      { x: '-100vw', y: '30vh'  },
      { x: '100vw',  y: '60vh'  },
      { x: '-80vw',  y: '-80vh' },
      { x: '90vw',   y: '-20vh' },
      { x: '-110vw', y: '70vh'  },
      { x: '110vw',  y: '40vh'  },
    ];
    const origin = scatterOrigins[index % scatterOrigins.length];

    // Start from scatter position
    setStyle({
      opacity: 0,
      transform: `translate(${origin.x}, ${origin.y}) scale(0.4) rotate(${(index % 2 === 0 ? 1 : -1) * (15 + index * 4)}deg)`,
      transition: 'none',
      pointerEvents: 'none',
    });

    // After one frame, animate to final position
    const t1 = setTimeout(() => {
      setStyle({
        opacity: 1,
        transform: 'translate(0,0) scale(1) rotate(0deg)',
        transition: `transform ${0.55 + index * 0.04}s cubic-bezier(.22,1,.36,1) ${delay + index * 0.08}s, opacity 0.3s ease ${delay + index * 0.08}s`,
        pointerEvents: 'all',
      });
    }, 30);

    const t2 = setTimeout(() => setSettled(true), (delay + index * 0.08 + 0.65) * 1000);

    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [panelOpen, index, delay]);

  return (
    <div ref={cardRef} style={style}>
      {children}
    </div>
  );
};

// ─────────────────────────────────────────────
// REUSABLE: XP BAR
// ─────────────────────────────────────────────
const XPBar = ({ current, max, level }) => {
  const pct = Math.min((current / max) * 100, 100);
  return (
    <div style={{ width: '100%' }}>
      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4, fontSize:11, color:'#94A3B8', fontFamily:'Orbitron, monospace', letterSpacing:'0.05em' }}>
        <span>LVL {level}</span>
        <span>{current}/{max} XP</span>
      </div>
      <div style={{ height:10, borderRadius:99, background:'rgba(255,255,255,0.08)', overflow:'hidden', boxShadow:'inset 0 1px 3px rgba(0,0,0,0.4)', border:'1px solid rgba(255,255,255,0.06)' }}>
        <div style={{
          height:'100%', width:`${pct}%`, borderRadius:99,
          background:'linear-gradient(90deg, #FF6B2B, #FFD700)',
          boxShadow:'0 0 12px #FF6B2BAA',
          transition:'width 1.2s cubic-bezier(.22,1,.36,1)',
          position:'relative',
        }}>
          <div style={{ position:'absolute', right:0, top:-1, bottom:-1, width:4, borderRadius:99, background:'#fff', opacity:0.7, filter:'blur(1px)' }} />
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// REUSABLE: CURRENCY DISPLAY
// ─────────────────────────────────────────────
const CurrencyDisplay = ({ coins, gems, coinDisplayRef }) => (
  <div style={{ display:'flex', gap:10 }}>
    <div
      ref={coinDisplayRef}
      style={{
        display:'flex', alignItems:'center', gap:5, padding:'4px 10px',
        borderRadius:20, background:'rgba(255,215,0,0.12)', border:'1px solid #FFD70033',
        fontSize:13, fontWeight:700, color:'#FFD700', fontFamily:'Orbitron, monospace',
        transition:'transform 0.15s ease, box-shadow 0.15s ease',
      }}
    >
      <span style={{ fontSize:14 }}>🪙</span>
      <span>{coins?.toLocaleString() || 0}</span>
    </div>
    <div style={{
      display:'flex', alignItems:'center', gap:5, padding:'4px 10px',
      borderRadius:20, background:'rgba(78,205,196,0.12)', border:'1px solid #4ECDC433',
      fontSize:13, fontWeight:700, color:'#4ECDC4', fontFamily:'Orbitron, monospace',
    }}>
      <span style={{ fontSize:14 }}>💎</span>
      <span>{gems?.toLocaleString() || 0}</span>
    </div>
  </div>
);

// ─────────────────────────────────────────────
// REUSABLE: GAME BUTTON
// ─────────────────────────────────────────────
const GameButton = ({ children, onClick, color = '#FF6B2B', size = 'md', glow, disabled, style }) => {
  const [pressed, setPressed] = useState(false);
  const sizes = { sm: { padding:'6px 14px', fontSize:12 }, md: { padding:'10px 22px', fontSize:14 }, lg: { padding:'14px 32px', fontSize:16 } };
  return (
    <button
      onClick={() => { if (!disabled) { SoundEngine.play('click'); onClick?.(); } }}
      onMouseEnter={() => SoundEngine.play('hover')}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      onMouseLeave={() => setPressed(false)}
      disabled={disabled}
      style={{
        ...sizes[size],
        fontFamily:'Orbitron, monospace', fontWeight:700, letterSpacing:'0.08em',
        color:'#fff', border:'none', borderRadius:10, cursor: disabled ? 'not-allowed' : 'pointer',
        background: disabled ? '#333' : `linear-gradient(135deg, ${color}, ${color}BB)`,
        boxShadow: disabled ? 'none' : `0 4px 18px ${glow || color}55, inset 0 1px 0 rgba(255,255,255,0.25)`,
        transform: pressed ? 'scale(0.95) translateY(2px)' : 'scale(1)',
        transition:'transform 0.1s, box-shadow 0.2s',
        opacity: disabled ? 0.5 : 1,
        ...style,
      }}
    >
      {children}
    </button>
  );
};

// ─────────────────────────────────────────────
// REUSABLE: ACHIEVEMENT POPUP
// ─────────────────────────────────────────────
const AchievementPopup = ({ badge, onClose }) => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    SoundEngine.play('success');
    setTimeout(() => setVisible(true), 50);
    const t = setTimeout(() => { setVisible(false); setTimeout(onClose, 400); }, 4500);
    return () => clearTimeout(t);
  }, []);
  return (
    <div style={{
      position:'fixed', bottom:24, right:24, zIndex:9999,
      transform: visible ? 'translateY(0) scale(1)' : 'translateY(60px) scale(0.8)',
      opacity: visible ? 1 : 0,
      transition:'all 0.4s cubic-bezier(.22,1,.36,1)',
    }}>
      <div style={{
        background:'linear-gradient(135deg, #1A1A2E, #16213E)',
        border:`2px solid ${badge.color}88`, borderRadius:16,
        padding:'16px 22px', display:'flex', alignItems:'center', gap:14,
        boxShadow:`0 8px 40px ${badge.color}44, 0 0 0 1px rgba(255,255,255,0.06)`,
        minWidth:280,
      }}>
        <div style={{
          width:54, height:54, borderRadius:50, display:'flex', alignItems:'center', justifyContent:'center',
          background:`radial-gradient(circle, ${badge.color}33, transparent)`,
          border:`2px solid ${badge.color}88`, fontSize:28,
          animation:'spin-slow 3s linear infinite',
        }}>{badge.icon}</div>
        <div>
          <div style={{ fontSize:10, fontFamily:'Orbitron,monospace', color:'#94A3B8', letterSpacing:'0.15em', marginBottom:3 }}>ACHIEVEMENT UNLOCKED</div>
          <div style={{ fontSize:16, fontWeight:800, color:'#fff', fontFamily:'Orbitron,monospace' }}>{badge.name}</div>
          <div style={{ fontSize:11, color: badge.color, marginTop:2, textTransform:'uppercase', letterSpacing:'0.08em' }}>{badge.rarity}</div>
        </div>
        <button onClick={onClose} style={{ marginLeft:'auto', background:'none', border:'none', color:'#64748B', cursor:'pointer', fontSize:18, lineHeight:1 }}>✕</button>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// ANIMATED PANEL (slide-up modal)
// ─────────────────────────────────────────────
const AnimatedPanel = ({ open, onClose, title, children, accentColor = '#FF6B2B' }) => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (open) { setTimeout(() => setVisible(true), 20); SoundEngine.play('open'); }
    else { setVisible(false); }
  }, [open]);
  if (!open && !visible) return null;
  return (
    <div style={{
      position:'fixed', inset:0, zIndex:500,
      background: visible ? 'rgba(0,0,0,0.75)' : 'rgba(0,0,0,0)',
      backdropFilter: visible ? 'blur(8px)' : 'none',
      transition:'background 0.3s, backdrop-filter 0.3s',
      display:'flex', alignItems:'flex-end', justifyContent:'center',
    }} onClick={() => { setVisible(false); setTimeout(onClose, 350); SoundEngine.play('close'); }}>
      <div style={{
        width:'100%', maxWidth:620,
        background:'linear-gradient(180deg, #0F172A 0%, #1A1A2E 100%)',
        borderRadius:'24px 24px 0 0',
        border:`1px solid ${accentColor}33`,
        borderBottom:'none',
        boxShadow:`0 -8px 60px ${accentColor}22, 0 0 0 1px rgba(255,255,255,0.05)`,
        maxHeight:'82vh', overflow:'hidden',
        transform: visible ? 'translateY(0)' : 'translateY(100%)',
        transition:'transform 0.45s cubic-bezier(.22,1,.36,1)',
      }} onClick={e => e.stopPropagation()}>
        <div style={{
          padding:'16px 22px', display:'flex', alignItems:'center', justifyContent:'space-between',
          borderBottom:`1px solid ${accentColor}22`,
          background:`linear-gradient(90deg, ${accentColor}12, transparent)`,
        }}>
          <div style={{ fontSize:16, fontWeight:800, fontFamily:'Orbitron,monospace', color:'#fff', letterSpacing:'0.05em' }}>{title}</div>
          <button onClick={() => { setVisible(false); setTimeout(onClose, 350); SoundEngine.play('close'); }}
            style={{ background:`${accentColor}22`, border:`1px solid ${accentColor}44`, borderRadius:8, color:'#fff', width:32, height:32, cursor:'pointer', fontSize:16 }}>✕</button>
        </div>
        <div style={{ overflowY:'auto', maxHeight:'72vh', padding:'20px 22px 32px' }}>{children}</div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// MENU NODE (the big buttons on home screen)
// ─────────────────────────────────────────────
const MenuNode = ({ item, onClick, index, unlocked = true }) => {
  const [hovered, setHovered] = useState(false);
  const [pressed, setPressed] = useState(false);
  const delay = `${index * 0.07}s`;

  return (
    <div
      onClick={() => { if (unlocked) { SoundEngine.play('select'); onClick(item); } else SoundEngine.play('click'); }}
      onMouseEnter={() => { setHovered(true); if (unlocked) SoundEngine.play('hover'); }}
      onMouseLeave={() => { setHovered(false); setPressed(false); }}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      style={{
        position:'relative', cursor: unlocked ? 'pointer' : 'not-allowed',
        display:'flex', flexDirection:'column', alignItems:'center', gap:8,
        animation:`nodeEntrance 0.6s cubic-bezier(.22,1,.36,1) ${delay} both`,
      }}
    >
      <div style={{
        position:'absolute', inset:-8, borderRadius:'50%',
        background:`radial-gradient(circle, ${item.glow}22, transparent 70%)`,
        opacity: hovered ? 1 : 0,
        transition:'opacity 0.3s',
        pointerEvents:'none',
      }} />
      <div style={{
        width:82, height:82, borderRadius:24,
        background: unlocked
          ? `linear-gradient(145deg, ${item.color}CC, ${item.color}66)`
          : 'linear-gradient(145deg, #1E293B, #0F172A)',
        border: `2px solid ${unlocked ? item.color : '#334155'}88`,
        display:'flex', alignItems:'center', justifyContent:'center',
        fontSize:34,
        boxShadow: hovered && unlocked
          ? `0 0 0 3px ${item.glow}55, 0 8px 32px ${item.glow}55, inset 0 1px 0 rgba(255,255,255,0.25)`
          : `0 4px 18px ${item.color}33, inset 0 1px 0 rgba(255,255,255,0.12)`,
        transform: pressed ? 'scale(0.9) translateY(3px)' : hovered ? 'scale(1.08) translateY(-3px)' : 'scale(1)',
        transition:'all 0.2s cubic-bezier(.22,1,.36,1)',
        filter: unlocked ? 'none' : 'grayscale(0.7)',
        position:'relative', overflow:'hidden',
      }}>
        <div style={{
          position:'absolute', top:0, left:0, right:0, height:'45%', borderRadius:'22px 22px 60% 60%',
          background:'linear-gradient(180deg, rgba(255,255,255,0.22), transparent)',
          pointerEvents:'none',
        }} />
        {item.icon}
        {!unlocked && (
          <div style={{ position:'absolute', inset:0, background:'rgba(0,0,0,0.55)', borderRadius:22, display:'flex', alignItems:'center', justifyContent:'center', fontSize:20 }}>🔒</div>
        )}
      </div>
      <div style={{ textAlign:'center' }}>
        <div style={{ fontSize:11, fontWeight:800, fontFamily:'Orbitron,monospace', letterSpacing:'0.08em', color: unlocked ? '#fff' : '#475569' }}>{item.label}</div>
        <div style={{ fontSize:9, color: unlocked ? item.color : '#334155', letterSpacing:'0.05em', marginTop:1 }}>{item.sub}</div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// ROAD ANIMATION
// ─────────────────────────────────────────────
const RoadDeco = () => (
  <div style={{ position:'fixed', bottom:0, left:0, right:0, height:70, pointerEvents:'none', zIndex:1, overflow:'hidden' }}>
    <div style={{ position:'absolute', bottom:0, left:0, right:0, height:40, background:'linear-gradient(180deg, transparent, #0A0E1A)' }} />
    <div style={{ position:'absolute', bottom:14, left:0, right:0, height:4, background:'linear-gradient(90deg, transparent, #FF6B2B44, #FFD70044, #FF6B2B44, transparent)' }} />
    <div style={{ position:'absolute', bottom:26, left:0, right:0, height:2, background:'rgba(255,255,255,0.04)' }} />
    <div style={{ position:'absolute', bottom:18, left:0, right:0, height:2, overflow:'hidden' }}>
      {Array.from({length:12},(_,i)=>(
        <div key={i} style={{
          position:'absolute', width:40, height:2, background:'rgba(255,215,0,0.35)', borderRadius:2,
          left:`${i*9}%`, animation:`dashMove 2.5s linear infinite`, animationDelay:`${-i*0.2}s`,
        }}/>
      ))}
    </div>
  </div>
);

// ─────────────────────────────────────────────
// RANK CARD
// ─────────────────────────────────────────────
const RankCard = ({ icon, label, rank, pct, sub, color }) => (
  <div style={{
    background:'rgba(255,255,255,0.04)', border:`1px solid ${color}33`,
    borderRadius:14, padding:'14px 16px', display:'flex', alignItems:'center', gap:14,
    boxShadow:`inset 0 1px 0 rgba(255,255,255,0.06)`,
  }}>
    <div style={{ fontSize:28, width:40, textAlign:'center' }}>{icon}</div>
    <div style={{ flex:1 }}>
      <div style={{ fontSize:10, color:'#64748B', fontFamily:'Orbitron,monospace', letterSpacing:'0.1em', marginBottom:4 }}>{label.toUpperCase()}</div>
      <div style={{ height:5, background:'rgba(255,255,255,0.06)', borderRadius:99, overflow:'hidden', marginBottom:4 }}>
        <div style={{ height:'100%', width:`${pct}%`, background:`linear-gradient(90deg, ${color}, ${color}AA)`, borderRadius:99, boxShadow:`0 0 6px ${color}` }} />
      </div>
      <div style={{ fontSize:10, color:'#94A3B8' }}>{sub}</div>
    </div>
    <div style={{ textAlign:'right' }}>
      <div style={{ fontSize:22, fontWeight:900, fontFamily:'Orbitron,monospace', color, lineHeight:1 }}>#{rank}</div>
      <div style={{ fontSize:10, color:'#64748B' }}>{pct}%ile</div>
    </div>
  </div>
);

// ─────────────────────────────────────────────
// BADGE GRID — with fly-in per card
// ─────────────────────────────────────────────
const BadgeGrid = ({ badges, earnedBadges, panelOpen }) => {
  const rarityColors = { common:'#8B9BB4', rare:'#4A90E2', epic:'#9B6FD8', legendary:'#FFD700' };
  return (
    <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12 }}>
      {Object.entries(BADGES).map(([key, badge], i) => {
        const earned = earnedBadges?.[key]?.earned;
        return (
          <CardFlyIn key={key} index={i} panelOpen={panelOpen} delay={0.05}>
            <div style={{ textAlign:'center', cursor:'pointer' }}
              onClick={() => earned && SoundEngine.play('coin')}>
              <div style={{
                width:56, height:56, margin:'0 auto 6px',
                borderRadius:16, display:'flex', alignItems:'center', justifyContent:'center',
                fontSize:26,
                background: earned ? `radial-gradient(circle, ${badge.color}33, ${badge.color}11)` : 'rgba(255,255,255,0.04)',
                border:`2px solid ${earned ? badge.color+'88' : '#1E293B'}`,
                boxShadow: earned ? `0 0 16px ${badge.color}44` : 'none',
                filter: earned ? 'none' : 'grayscale(1)',
                opacity: earned ? 1 : 0.45,
                position:'relative', overflow:'hidden',
              }}>
                {badge.icon}
                {earned && <div style={{ position:'absolute', inset:0, background:'linear-gradient(135deg, rgba(255,255,255,0.18) 0%, transparent 60%)', borderRadius:14 }} />}
              </div>
              <div style={{ fontSize:9, fontFamily:'Orbitron,monospace', color: earned ? '#E2E8F0' : '#334155', lineHeight:1.3 }}>{badge.name}</div>
              <div style={{ fontSize:8, color: rarityColors[badge.rarity], marginTop:2 }}>{badge.rarity}</div>
            </div>
          </CardFlyIn>
        );
      })}
    </div>
  );
};

// ─────────────────────────────────────────────
// SUBCONCEPT PROGRESS
// ─────────────────────────────────────────────
const ProgressPanel = ({ concepts, practiceMap }) => {
  const [expanded, setExpanded] = useState(null);
  const fmt = (n) => n.replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase());
  const filtered = concepts.filter(c=>(c.subconcepts||[]).some(sc=>sc.is_selected));
  if (!filtered.length) return <div style={{ textAlign:'center', color:'#64748B', padding:'40px 0', fontSize:14 }}>🎉 Nothing in progress right now.</div>;
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
      {filtered.map((concept,ci)=>{
        const subs = (concept.subconcepts||[]).filter(sc=>sc.is_selected);
        const open = expanded===ci;
        return (
          <div key={ci} style={{ border:'1px solid rgba(124,77,255,0.25)', borderRadius:14, overflow:'hidden' }}>
            <div style={{ padding:'12px 16px', display:'flex', justifyContent:'space-between', alignItems:'center', cursor:'pointer',
              background: open ? 'rgba(124,77,255,0.12)' : 'rgba(255,255,255,0.03)'}}
              onClick={()=>{setExpanded(open?null:ci); SoundEngine.play('select');}}>
              <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                <span style={{ fontSize:18 }}>⚡</span>
                <span style={{ fontSize:13, fontWeight:700, fontFamily:'Orbitron,monospace', color:'#E2E8F0' }}>{concept.concept_name}</span>
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                <span style={{ fontSize:10, color:'#7C4DFF', background:'rgba(124,77,255,0.15)', padding:'2px 8px', borderRadius:20 }}>{subs.length} topics</span>
                <span style={{ color:'#7C4DFF', fontSize:14, transform: open?'rotate(180deg)':'none', transition:'transform 0.3s' }}>▾</span>
              </div>
            </div>
            {open && (
              <div style={{ padding:'0 14px 14px', display:'flex', flexDirection:'column', gap:8 }}>
                {subs.map((sub,si)=>{
                  const pm = practiceMap[sub.subconcept_name]?.mastery||0;
                  const col = pm>=70?'#50C878':pm>=40?'#FFD700':'#FF6B6B';
                  return (
                    <div key={si} style={{ background:'rgba(255,255,255,0.03)', borderRadius:10, padding:'10px 14px', border:'1px solid rgba(255,255,255,0.06)' }}>
                      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
                        <span style={{ fontSize:12, color:'#CBD5E1' }}>{fmt(sub.subconcept_name)}</span>
                        <span style={{ fontSize:13, fontWeight:700, fontFamily:'Orbitron,monospace', color:col }}>{Math.round(pm)}%</span>
                      </div>
                      <div style={{ height:4, background:'rgba(255,255,255,0.07)', borderRadius:99, overflow:'hidden' }}>
                        <div style={{ height:'100%', width:`${Math.min(pm,100)}%`, background:col, borderRadius:99, boxShadow:`0 0 6px ${col}` }} />
                      </div>
                      {sub.needs_practice&&<div style={{ fontSize:10, color:'#FF6B6B', marginTop:6 }}>⚠️ {sub.practice_reason||'Needs Practice'}</div>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ─────────────────────────────────────────────
// SETTINGS PANEL
// ─────────────────────────────────────────────
const SettingsPanel = ({ audioEnabled, onToggleAudio }) => (
  <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
    {[
      { label:'Sound Effects', desc:'Game audio & button sounds', value:audioEnabled, toggle:onToggleAudio, icon:'🔊' },
      { label:'Notifications', desc:'Achievement & streak alerts', value:true, toggle:()=>{}, icon:'🔔' },
      { label:'Dark Mode', desc:'Always on for best experience', value:true, toggle:()=>{}, icon:'🌙', locked:true },
    ].map(item=>(
      <div key={item.label} style={{
        display:'flex', alignItems:'center', justifyContent:'space-between',
        background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:14, padding:'14px 18px',
      }}>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <span style={{ fontSize:22 }}>{item.icon}</span>
          <div>
            <div style={{ fontSize:13, fontWeight:700, color:'#E2E8F0', fontFamily:'Orbitron,monospace' }}>{item.label}</div>
            <div style={{ fontSize:11, color:'#64748B', marginTop:2 }}>{item.desc}</div>
          </div>
        </div>
        <div onClick={()=>!item.locked&&item.toggle()} style={{
          width:46, height:26, borderRadius:99, cursor: item.locked?'not-allowed':'pointer',
          background: item.value ? 'linear-gradient(90deg,#FF6B2B,#FFD700)' : '#1E293B',
          border:'1px solid rgba(255,255,255,0.1)', position:'relative', transition:'background 0.3s',
          boxShadow: item.value ? '0 0 10px #FF6B2B66' : 'none',
          opacity: item.locked ? 0.5 : 1,
        }}>
          <div style={{
            position:'absolute', top:2, left: item.value?'calc(100% - 22px)':2, width:20, height:20, borderRadius:'50%',
            background:'#fff', transition:'left 0.25s cubic-bezier(.22,1,.36,1)',
            boxShadow:'0 1px 4px rgba(0,0,0,0.4)',
          }}/>
        </div>
      </div>
    ))}
    <div style={{ height:1, background:'rgba(255,255,255,0.06)', margin:'4px 0' }} />
    <div style={{ fontSize:10, color:'#334155', fontFamily:'Orbitron,monospace', textAlign:'center', letterSpacing:'0.12em' }}>v2.0.0 · GAME DASHBOARD</div>
  </div>
);

// ─────────────────────────────────────────────
// REWARDS PANEL CONTENT — rank cards with fly-in
// ─────────────────────────────────────────────
const RewardsPanelContent = ({ badges, rankCards, panelOpen }) => {
  const earnedCount = Object.values(badges).filter(b => b?.earned).length;
  const totalCount  = Object.keys(BADGES).length;

  return (
    <div>
      <div style={{ marginBottom:20 }}>
        <div style={{ fontSize:11, fontFamily:'Orbitron,monospace', color:'#64748B', letterSpacing:'0.1em', marginBottom:12 }}>BADGE COLLECTION</div>
        <div style={{ height:6, background:'rgba(255,255,255,0.07)', borderRadius:99, marginBottom:4, overflow:'hidden' }}>
          <div style={{
            height:'100%',
            width: panelOpen ? `${(earnedCount / totalCount) * 100}%` : '0%',
            background:'linear-gradient(90deg,#FF6B2B,#FFD700)', borderRadius:99,
            boxShadow:'0 0 10px #FFD70066',
            transition: 'width 1.2s cubic-bezier(.22,1,.36,1) 0.3s',
          }} />
        </div>
        <div style={{ fontSize:10, color:'#64748B', textAlign:'right', marginBottom:16 }}>
          {earnedCount}/{totalCount} Earned
        </div>
        <BadgeGrid badges={BADGES} earnedBadges={badges} panelOpen={panelOpen} />
      </div>

      <div style={{ height:1, background:'rgba(255,255,255,0.06)', margin:'20px 0' }} />
      <div style={{ fontSize:11, fontFamily:'Orbitron,monospace', color:'#64748B', letterSpacing:'0.1em', marginBottom:12 }}>PERFORMANCE</div>

      <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
        {rankCards.map((r, i) => (
          <CardFlyIn key={i} index={Object.keys(BADGES).length + i} panelOpen={panelOpen} delay={0.05}>
            <RankCard {...r} />
          </CardFlyIn>
        ))}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────
const StudentDashboard = () => {
  const navigate = useNavigate();
  const [dashData, setDashData] = useState({ rankings:{}, metrics:{}, priority:{}, hierarchicalConcepts:[], badges:{}, subconceptMastery:[] });
  const [practiceMap, setPracticeMap] = useState({});
  const [user, setUser] = useState({ name:'', grade:'', avatar_emoji:'🏎️' });
  const [loading, setLoading] = useState(true);
  const [audioOn, setAudioOn] = useState(true);
  const [openPanel, setOpenPanel] = useState(null);
  const [achievement, setAchievement] = useState(null);
  const [coins, setCoins] = useState(0);
  const [gems, setGems] = useState(0);
  const [showSplash, setShowSplash] = useState(true);
  const [entered, setEntered] = useState(false);
  const coinsFiredRef = useRef(false);

  const { coinDisplayRef, flyCoinsToTarget } = useCoinFlyAnimation();

  // Fetch data
  useEffect(() => {
    (async () => {
      try {
        const [profileRes,rankRes,metricsRes,priorityRes,hierRes,badgesRes,subRes] = await Promise.all([
          fetch('/api/profile'), fetch('/api/subconcept-comprehensive-rankings'),
          fetch('/api/student-success-metrics-enhanced'), fetch('/api/priority-practice-concepts'),
          fetch('/api/hierarchical-concept-progress'), fetch('/api/user-badges'),
          fetch('/api/selected-subconcept-mastery'),
        ]);
        const [profile,rank,metrics,priority,hier,badges,sub] = await Promise.all([
          profileRes.json(),rankRes.json(),metricsRes.json(),priorityRes.json(),hierRes.json(),badgesRes.json(),subRes.json()
        ]);
        if (profile.success&&profile.user) setUser({ name:profile.user.name, grade:profile.user.grade, avatar_emoji:profile.user.avatar_emoji||'🏎️' });
        if (sub.success) {
          const map={};
          sub.concepts.forEach(item=>{ map[item.subconcept_name]={ mastery:item.practice_mastery||0, accuracy:item.practice_accuracy||0 }; });
          setPracticeMap(map);
        }
        const m = metrics.success ? metrics.metrics : {};
        setCoins(Math.floor((m.mastery_level||0)*12 + (m.accuracy||0)*8));
        setGems(Math.floor((m.streak||0)*3 + (m.rank ? 50-Math.min(m.rank,50) : 0)));
        setDashData({ rankings:rank.success?rank.rankings:{}, metrics:m, priority:priority.success?priority:{}, hierarchicalConcepts:hier.success?hier.concepts:[], badges:badges.success?badges.badges:{}, subconceptMastery:sub.success?sub.concepts:[] });
        if (badges.success) {
          const shownBadges = JSON.parse(localStorage.getItem('shown_badges_v2')||'{}');
          for (const [key,badge] of Object.entries(BADGES)) {
            if (badges.badges[key]?.earned && !shownBadges[key]) {
              shownBadges[key]=true;
              localStorage.setItem('shown_badges_v2',JSON.stringify(shownBadges));
              setTimeout(()=>setAchievement(badge), 1000); break;
            }
          }
        }
      } catch(e){ console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  // 🪙 Fire coin animation once after splash screen is dismissed and data is ready
  useEffect(() => {
    if (!showSplash && !loading && !coinsFiredRef.current && coins > 0) {
      coinsFiredRef.current = true;
      // Small delay so the layout settles first
      setTimeout(() => {
        flyCoinsToTarget(18);
      }, 500);
    }
  }, [showSplash, loading, coins, flyCoinsToTarget]);

  const handleToggleAudio = () => {
    const on = SoundEngine.toggle();
    setAudioOn(on);
  };

  const handleMenuClick = (item) => {
    if (item.panel) { setOpenPanel(item.panel); return; }
    if (!item.route) return;
    if (item.id === 'garage') {
      const hasSelected = dashData.hierarchicalConcepts.some(c=>(c.subconcepts||[]).some(s=>s.is_selected));
      if (!hasSelected) { navigate('/concept-selection'); return; }
    }
    navigate(item.route);
  };

  const { rankings, metrics, priority, hierarchicalConcepts, badges } = dashData;
  const xp = Math.floor((metrics.mastery_level||0)*10);
  const level = Math.floor((metrics.mastery_level||0)/10);
  const rankCards = [
    { icon:'🏆', label:'Overall',  rank:rankings.overall?.rank||'-',  pct:rankings.overall?.percentage||0,  sub:`#${metrics.rank||'-'} global`,   color:'#FFD700' },
    { icon:'🔥', label:'Streak',   rank:rankings.streak?.rank||'-',   pct:rankings.streak?.percentage||0,   sub:`${metrics.streak||0}d streak`,   color:'#FF6B2B' },
    { icon:'🎯', label:'Accuracy', rank:rankings.accuracy?.rank||'-', pct:rankings.accuracy?.percentage||0, sub:`${metrics.accuracy||0}% acc`,    color:'#50C878' },
    { icon:'⚡', label:'Speed',    rank:rankings.speed?.rank||'-',    pct:rankings.speed?.percentage||0,    sub:`${metrics.avg_time||0}s avg`,    color:'#4ECDC4' },
  ];

  // Splash screen
  if (loading || showSplash) return (
    <div style={{ minHeight:'100vh', background:'#070B14', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:24 }} onClick={() => { if (!loading) { SoundEngine.play('engine'); setShowSplash(false); setTimeout(()=>setEntered(true),50); } }}>
      <div style={{ fontSize:72, animation:'bounce 1s ease-in-out infinite' }}>🏎️</div>
      <div style={{ fontFamily:'Orbitron,monospace', fontSize:28, fontWeight:900, letterSpacing:'0.15em',
        background:'linear-gradient(90deg,#FF6B2B,#FFD700,#FF4081)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent' }}>
        RACE ACADEMY
      </div>
      {loading
        ? <div style={{ color:'#334155', fontFamily:'Orbitron,monospace', fontSize:11, letterSpacing:'0.2em', animation:'pulse 1.5s ease-in-out infinite' }}>LOADING...</div>
        : <div style={{ color:'#FF6B2B', fontFamily:'Orbitron,monospace', fontSize:12, letterSpacing:'0.2em', animation:'pulse 1.2s ease-in-out infinite' }}>TAP TO START</div>
      }
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;700;900&display=swap');
        @keyframes bounce { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-16px)} }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.3} }
      `}</style>
    </div>
  );

  return (
    <div style={{ minHeight:'100vh', background:'#070B14', color:'#fff', fontFamily:'system-ui,sans-serif', position:'relative', overflowX:'hidden', paddingBottom:80 }}>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;700;900&display=swap');
        @keyframes nodeEntrance {
          from { opacity:0; transform:scale(0.6) translateY(20px); }
          to   { opacity:1; transform:scale(1) translateY(0); }
        }
        @keyframes floatBob {
          0%,100%{transform:translateY(0)} 50%{transform:translateY(-8px)}
        }
        @keyframes dashMove {
          from{transform:translateX(-100px)} to{transform:translateX(calc(100vw + 100px))}
        }
        @keyframes spin-slow {
          from{transform:rotate(0deg)} to{transform:rotate(360deg)}
        }
        @keyframes glowPulse {
          0%,100%{box-shadow:0 0 20px #FF6B2B44} 50%{box-shadow:0 0 40px #FF6B2BAA}
        }
        @keyframes bgShift {
          0%{background-position:0% 50%} 50%{background-position:100% 50%} 100%{background-position:0% 50%}
        }
        @keyframes entranceDown {
          from{opacity:0;transform:translateY(-24px)} to{opacity:1;transform:translateY(0)}
        }
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { width:4px; }
        ::-webkit-scrollbar-track { background:transparent; }
        ::-webkit-scrollbar-thumb { background:#FF6B2B44; border-radius:99px; }
      `}</style>

      <ParticleField />

      <div style={{
        position:'fixed', inset:0, zIndex:0, pointerEvents:'none',
        background:'radial-gradient(ellipse 80% 60% at 10% 20%, #FF6B2B0D 0%, transparent 50%), radial-gradient(ellipse 60% 80% at 90% 80%, #7C4DFF0D 0%, transparent 50%), radial-gradient(ellipse 50% 50% at 50% 0%, #FFD7000A 0%, transparent 50%)',
      }} />

      {/* ── TOP BAR ── */}
      <div style={{
        position:'sticky', top:0, zIndex:200,
        background:'rgba(7,11,20,0.85)', backdropFilter:'blur(16px)',
        borderBottom:'1px solid rgba(255,255,255,0.06)',
        padding:'10px 18px', display:'flex', alignItems:'center', justifyContent:'space-between',
        animation:'entranceDown 0.5s ease both',
      }}>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <div style={{
            width:44, height:44, borderRadius:14, background:'linear-gradient(135deg,#FF6B2B,#FFD700)',
            display:'flex', alignItems:'center', justifyContent:'center', fontSize:22,
            boxShadow:'0 0 16px #FF6B2B66', animation:'glowPulse 2.5s ease-in-out infinite',
          }}>{user.avatar_emoji}</div>
          <div>
            <div style={{ fontSize:13, fontWeight:800, fontFamily:'Orbitron,monospace', letterSpacing:'0.05em', lineHeight:1.2 }}>{user.name||'Racer'}</div>
            <div style={{ fontSize:10, color:'#FF6B2B', letterSpacing:'0.08em' }}>GRADE {user.grade} · LV.{level}</div>
          </div>
        </div>

        {/* Pass the ref so coins know WHERE to fly */}
        <CurrencyDisplay coins={coins} gems={gems} coinDisplayRef={coinDisplayRef} />

        <button onClick={()=>{SoundEngine.play('click'); navigate('/parent-dashboard');}}
          style={{ background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:10, color:'#94A3B8', padding:'6px 12px', fontSize:11, cursor:'pointer', fontFamily:'Orbitron,monospace', letterSpacing:'0.06em', whiteSpace:'nowrap' }}>
          👨‍👩‍👧 PARENT
        </button>
      </div>

      <div style={{ position:'relative', zIndex:10, padding:'24px 20px 0', width:'100%' }}>

        <div style={{ marginBottom:28, animation:'entranceDown 0.6s 0.1s ease both', opacity:0, animationFillMode:'forwards' }}>
          <XPBar current={xp % 1000} max={1000} level={level} />
        </div>

        <div style={{ textAlign:'center', marginBottom:32, animation:'entranceDown 0.6s 0.15s ease both', opacity:0, animationFillMode:'forwards' }}>
          <div style={{
            fontSize:28, fontWeight:900, fontFamily:'Orbitron,monospace', letterSpacing:'0.1em',
            background:'linear-gradient(90deg,#FF6B2B,#FFD700,#FF4081)',
            WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent',
            backgroundSize:'200% 100%', animation:'bgShift 4s linear infinite',
          }}>
            GAME HQ
          </div>
          <div style={{ fontSize:10, color:'#475569', letterSpacing:'0.2em', marginTop:4, fontFamily:'Orbitron,monospace' }}>SELECT YOUR DESTINATION</div>
        </div>

        <div style={{ display:'grid', gridTemplateColumns:'repeat(3,minmax(100px,140px))', gap:24, justifyContent:'center', marginBottom:32 }}>
          {MENU_ITEMS.map((item, i) => (
            <MenuNode key={item.id} item={item} index={i} onClick={handleMenuClick} unlocked={!item.locked} />
          ))}
        </div>

        <div style={{
          display:'flex', gap:10, marginBottom:24, width:'100%',
          animation:`nodeEntrance 0.6s 0.5s ease both`, opacity:0, animationFillMode:'forwards',
        }}>
          {[
            { label:'STREAK', val:`${metrics.streak||0}d`, icon:'🔥', color:'#FF6B2B' },
            { label:'ACCURACY', val:`${metrics.accuracy||0}%`, icon:'🎯', color:'#50C878' },
            { label:'RANK', val:`#${metrics.rank||'-'}`, icon:'👑', color:'#FFD700' },
          ].map(s=>(
            <div key={s.label} style={{
              flex:1, background:'rgba(255,255,255,0.04)', borderRadius:14, padding:'12px 10px', textAlign:'center',
              border:`1px solid ${s.color}22`, boxShadow:`inset 0 1px 0 rgba(255,255,255,0.05)`,
            }}>
              <div style={{ fontSize:20, marginBottom:4 }}>{s.icon}</div>
              <div style={{ fontSize:16, fontWeight:900, fontFamily:'Orbitron,monospace', color:s.color, lineHeight:1 }}>{s.val}</div>
              <div style={{ fontSize:9, color:'#475569', marginTop:4, letterSpacing:'0.1em' }}>{s.label}</div>
            </div>
          ))}
        </div>

        {(priority.total_concepts_needing_practice||0) > 0 && (
          <div style={{
            background:'linear-gradient(135deg, rgba(255,107,43,0.15), rgba(255,215,0,0.08))',
            border:'1px solid #FF6B2B44', borderRadius:14, padding:'14px 18px',
            display:'flex', alignItems:'center', justifyContent:'space-between',
            marginBottom:24,
            animation:`nodeEntrance 0.6s 0.6s ease both`, opacity:0, animationFillMode:'forwards',
          }}>
            <div style={{ display:'flex', alignItems:'center', gap:12 }}>
              <span style={{ fontSize:24 }}>⚠️</span>
              <div>
                <div style={{ fontSize:12, fontWeight:700, fontFamily:'Orbitron,monospace', color:'#FF6B2B' }}>
                  {priority.total_concepts_needing_practice} CONCEPTS NEED WORK
                </div>
                <div style={{ fontSize:11, color:'#64748B', marginTop:2 }}>Practice to level up faster</div>
              </div>
            </div>
            <GameButton size="sm" color="#FF6B2B" onClick={()=>navigate('/select-practice-concept')}>GO →</GameButton>
          </div>
        )}

      </div>

      <RoadDeco />

      {/* ──────── PANELS ──────── */}

      {/* Rewards Panel — cards fly in from outside screen */}
      <AnimatedPanel open={openPanel==='rewards'} onClose={()=>setOpenPanel(null)} title="🏆 Rewards & Achievements" accentColor="#FFD700">
        <RewardsPanelContent
          badges={badges}
          rankCards={rankCards}
          panelOpen={openPanel==='rewards'}
        />
      </AnimatedPanel>

      {/* Progress Panel */}
      <AnimatedPanel open={openPanel==='progress'} onClose={()=>setOpenPanel(null)} title="🗺️ Progress Map" accentColor="#7C4DFF">
        <ProgressPanel concepts={hierarchicalConcepts} practiceMap={practiceMap} />
      </AnimatedPanel>

      {/* Settings Panel */}
      <AnimatedPanel open={openPanel==='settings'} onClose={()=>setOpenPanel(null)} title="⚙️ Settings" accentColor="#78909C">
        <SettingsPanel audioEnabled={audioOn} onToggleAudio={handleToggleAudio} />
      </AnimatedPanel>

      {/* Achievement Toast */}
      {achievement && <AchievementPopup badge={achievement} onClose={()=>setAchievement(null)} />}

    </div>
  );
};

export default StudentDashboard;